import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Lock, Unlock, X, ChevronLeft, ChevronRight, MapPin, Users, Crosshair, BookOpen, Crown, Flame } from 'lucide-react';
import { eras } from '../data/universeData';

// Rich event data for each era
const ERA_EVENTS = {
  1: [
    { year: '1919', event: 'The Bullies of New York form — a multiracial Anti-Mafia criminal organization', icon: Users, type: 'faction' },
    { year: '1920s', event: 'Policy bank established across Harlem and Brooklyn — the origin of ALL KOW money', icon: Crown, type: 'event' },
    { year: '1930s', event: 'The four allied crews: Morelli Family, Harlem Hoodlums, Jam Rockers, Jaded Blade', icon: Users, type: 'faction' },
    { year: '1936', event: 'Maurice born — biracial son of Frankie Jr. and a Black waitress', icon: Users, type: 'character' },
    { year: '1936', event: '15-year-old Frankie Jr. running with the Harlem Hoodlums — jazz club night locks Morellis and Harlem together', icon: Crosshair, type: 'event' },
    { year: '1938', event: 'Leon introduces Frankie to Annora — she raises 2-year-old Maurice', icon: Users, type: 'character' },
    { year: '1938', event: 'Annora gets apartment in Flatbush — seeds planted in Brooklyn that grow for generations', icon: MapPin, type: 'location' },
    { year: '~1942', event: 'Annora meets Winston Johnston — Jamaican ganja smuggler from Spanishtown', icon: Users, type: 'character' },
    { year: '1942', event: 'Frankie Jr. and cousin Donny enlist in WW2', icon: Crosshair, type: 'event' },
    { year: '~1945', event: 'Francisco Moreno Sr. killed by The Table (The Commission)', icon: Flame, type: 'death' },
    { year: '1945', event: 'Tio Johnston born — son of Annora and Winston. The bridge between bloodlines.', icon: Users, type: 'character' },
  ],
  2: [
    { year: '1946', event: 'Frankie returns from WW2 — discovers the truth about Rizzo', icon: Crosshair, type: 'event' },
    { year: '1946', event: 'Frankie chooses silence — protects Rizzo, lets Morellis carry the blame', icon: BookOpen, type: 'event' },
    { year: '1940s', event: 'The Bullies network fractures — Hoodlums and Johnstons cut ties with Morellis', icon: Flame, type: 'war' },
    { year: '1940s', event: 'Maurice grows up in the Bronx — blending Italian and Spanish communities', icon: MapPin, type: 'location' },
    { year: '1950s', event: 'Policy bank money disperses through multiple hands, boroughs, generations', icon: Crown, type: 'event' },
    { year: '1950s', event: 'Rizzo eventually killed — the last threat — but the damage was permanent', icon: Flame, type: 'death' },
    { year: '1960s', event: 'Seeds in Flatbush take root — the borough that will birth CMD', icon: MapPin, type: 'location' },
  ],
  3: [
    { year: '1965', event: 'Antwan & 5-year-old Anton arrive from Jamaica at JFK', icon: MapPin, type: 'arrival' },
    { year: '1966', event: 'Meet Tio Johnston — MTA conductor & drug trafficker', icon: Users, type: 'character' },
    { year: '1968', event: 'Move to East New York, Brooklyn. Anton and Vincent bond.', icon: MapPin, type: 'location' },
    { year: '1970', event: 'Lt. Toribio Rodriguez joins NYPD after Vietnam — Pink Houses kid, lost cousins to the Morellis', icon: Crown, type: 'character' },
    { year: '1973', event: "Tio's double life — Polo Grounds connect with Shoggy", icon: Crosshair, type: 'event' },
    { year: '1975', event: 'Rodriguez makes major heroin bust connected to Morelli family — case suppressed', icon: Crosshair, type: 'event' },
    { year: '1975', event: 'A man in a black Cadillac crushes a chess rook in front of Rodriguez — Marco Morelli', icon: Flame, type: 'event' },
    { year: '1977', event: 'NYC Blackout — looting, chaos, opportunity', icon: Flame, type: 'event' },
    { year: '1979', event: "Anton's first taste of the streets", icon: Crosshair, type: 'event' },
  ],
  4: [
    { year: '1979', event: 'Tio murdered at the docks by the Ski Crew', icon: Crosshair, type: 'death' },
    { year: '1980', event: 'Vincent witnesses his father\'s death', icon: Users, type: 'event' },
    { year: '1981', event: 'Antwan takes over. Trains Anton and Vincent.', icon: Crown, type: 'character' },
    { year: '1982', event: 'CMD officially forms — Goonie, Dino, Manny recruited', icon: Users, type: 'faction' },
    { year: '1983', event: 'Anton sent to Spofford — brotherhood forged behind bars', icon: MapPin, type: 'location' },
    { year: '1984', event: "Tio's funeral — Black Israelites and De La Toros attend. Shoggy presses tribute into Vincent's hands.", icon: BookOpen, type: 'event' },
    { year: '1985', event: 'The Chainsaw Lesson. CMD destroys the Ski Crew.', icon: Flame, type: 'war' },
    { year: '1986', event: 'Ark arrives from Oklahoma with the first crack cook', icon: Crosshair, type: 'event' },
  ],
  5: [
    { year: '1987', event: 'CMD at peak power — controls all of Brooklyn', icon: Crown, type: 'event' },
    { year: '1988', event: 'Anton meets Claude Devoe — learns money laundering', icon: Users, type: 'character' },
    { year: '1988', event: 'The electronics store empire is born', icon: MapPin, type: 'location' },
    { year: '1989', event: 'Rodriguez descends — the Morellis took everything from Milford St, now he takes it back', icon: Crosshair, type: 'character' },
    { year: '1989', event: 'The Morelli crime family enters the picture', icon: Users, type: 'faction' },
    { year: '1990', event: "Francisco Morelli — Anton's dangerous ally", icon: Crown, type: 'character' },
    { year: '1990', event: 'Demon leads the Bronx campaign — White Christmas — kills Supremo', icon: Flame, type: 'war' },
    { year: '1990', event: 'Rodriguez — betrayed by Gloria, commits suicide New Year\'s Eve', icon: Flame, type: 'death' },
    { year: '1990', event: 'Vincent transfers CMD assets into South Brooklyn Logistics', icon: Crown, type: 'event' },
    { year: '1991', event: "The playground shooting — the code is broken", icon: Flame, type: 'event' },
  ],
  6: [
    { year: '1991', event: 'Francisco sentenced to life', icon: Crosshair, type: 'event' },
    { year: '1992', event: 'CMD scatters — Anton to Richmond, Vincent to Maryland', icon: MapPin, type: 'exile' },
    { year: '1993', event: "Rosa emerges as 'The Ghost of Sunset Park'", icon: Users, type: 'character' },
    { year: '1994', event: 'BGM forms in the Bronx. Los Diablos from California.', icon: Users, type: 'faction' },
    { year: '1995', event: 'Gloria discovers she\'s pregnant — Lena conceived', icon: BookOpen, type: 'event' },
    { year: '1996', event: 'PAW (Pills and Weed) forms', icon: Users, type: 'faction' },
    { year: '1997', event: 'Rico records first demo. The music scene shifts.', icon: BookOpen, type: 'event' },
  ],
  7: [
    { year: '1998', event: 'CMD ghosts reposition — electronics, real estate', icon: MapPin, type: 'event' },
    { year: '2000', event: 'A mysterious nightclub opens on the West Coast', icon: MapPin, type: 'location' },
    { year: '2001', event: 'MPG five introduced: Red Eye, Ty, Cash and others', icon: Users, type: 'faction' },
    { year: '2002', event: 'DDN (Dominican Dope Nation) rises under Raul', icon: Users, type: 'faction' },
    { year: '2003', event: 'Big Rich goes platinum', icon: Crown, type: 'event' },
    { year: '2005', event: 'MPG becomes transporters for DDN', icon: Crosshair, type: 'event' },
    { year: '2006', event: 'The ghosts begin to stir — old connections resurface in Brooklyn', icon: Flame, type: 'event' },
  ],
  8: [
    { year: '2006', event: 'V.I.P. nightclub opens in Los Angeles — ownership classified', icon: MapPin, type: 'location' },
    { year: '2007', event: 'The VIP table competition begins — monthly rankings drive the staff', icon: Crown, type: 'event' },
    { year: '2010', event: 'Amber Lane promoted to Assistant Manager — she sees everything', icon: Users, type: 'character' },
    { year: '2012', event: 'Marcus James hired as Head of Security — ex-military, no questions asked', icon: Users, type: 'character' },
    { year: '2015', event: 'Persian investors begin circling the club — they want ownership', icon: Crosshair, type: 'event' },
    { year: '2018', event: 'XL becomes the top promoter — but his connections run deeper than nightlife', icon: Flame, type: 'event' },
    { year: '2022', event: 'A face from Brooklyn appears at the bar — the past refuses to stay buried', icon: Flame, type: 'event' },
    { year: '2025', event: 'What happened in LA nightclubs explodes on the Brooklyn waterfront', icon: Crosshair, type: 'event' },
  ],
  9: [
    { year: '2026', event: 'A casino opens on the Brooklyn waterfront', icon: MapPin, type: 'location' },
    { year: '2026', event: 'A man walks out of prison after twenty years', icon: Crown, type: 'event' },
    { year: '2026', event: "Anton returns — not for revenge, but for Monopoly", icon: Flame, type: 'event' },
    { year: '2026', event: 'Juny emerges from military — double identity', icon: Users, type: 'character' },
    { year: '2026', event: 'YN Nation taps California network with CMD roots', icon: Crosshair, type: 'event' },
    { year: '2026', event: "Maurice's grandsons emerge — they want the Waterfront back. It was always built on Bullies money.", icon: Flame, type: 'event' },
  ],
};

const ERA_LOCATIONS = {
  1: 'Harlem — Brooklyn — The Bronx',
  2: 'Harlem — Brooklyn — The Bronx',
  3: 'East New York, Brooklyn',
  4: 'Brooklyn — Spofford — The Docks',
  5: 'Brooklyn + Queens + Staten Island + NJ',
  6: 'Richmond + Maryland + Virginia Beach',
  7: 'Oakland + NYC + National',
  8: 'Los Angeles, California',
  9: 'The Waterfront District, Brooklyn',
};

const ERA_COLORS = {
  1: '#D4A574',
  2: '#8B7355',
  3: '#C9A84C',
  4: '#4A7AB5',
  5: '#8B1A1A',
  6: '#9F7AEA',
  7: '#4ADE80',
  8: '#F59E0B',
  9: '#C9A84C',
};

const TYPE_COLORS = {
  arrival: '#4ADE80',
  character: '#4A7AB5',
  location: '#C9A84C',
  event: '#E8E0D0',
  death: '#8B1A1A',
  war: '#8B1A1A',
  faction: '#9F7AEA',
  expansion: '#4ADE80',
  exile: '#555',
};

const EventDot = ({ event, eraColor, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-20px" });
  const dotColor = TYPE_COLORS[event.type] || eraColor;
  const Icon = event.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="relative pl-8 pb-6 group"
    >
      {/* Connector line */}
      <div className="absolute left-[11px] top-3 bottom-0 w-px" style={{ backgroundColor: `${eraColor}15` }} />

      {/* Dot */}
      <div className="absolute left-0 top-1 w-6 h-6 rounded-full flex items-center justify-center border transition-all duration-300 group-hover:scale-125"
        style={{ backgroundColor: `${dotColor}15`, borderColor: `${dotColor}40` }}>
        <Icon size={10} style={{ color: dotColor }} />
      </div>

      {/* Content */}
      <div className="group-hover:translate-x-1 transition-transform duration-300">
        <span className="font-['Outfit'] text-[10px] tracking-widest block mb-0.5" style={{ color: `${eraColor}80` }}>
          {event.year}
        </span>
        <p className="font-['Outfit'] text-[#E8E0D0]/70 text-sm leading-relaxed group-hover:text-[#E8E0D0] transition-colors">
          {event.event}
        </p>
      </div>
    </motion.div>
  );
};

const TimelineSection = () => {
  const [selectedEra, setSelectedEra] = useState(null);
  const [activeEra, setActiveEra] = useState(1);
  const scrollRef = useRef(null);

  const scrollToEra = (direction) => {
    const nextEra = direction === 'next'
      ? Math.min(9, activeEra + 1)
      : Math.max(1, activeEra - 1);
    setActiveEra(nextEra);
  };

  const currentEraData = eras.find(e => e.id === activeEra);
  const currentEvents = ERA_EVENTS[activeEra] || [];
  const eraColor = ERA_COLORS[activeEra] || '#C9A84C';

  return (
    <section
      id="timeline"
      data-testid="timeline-section"
      className="py-24 px-6 bg-[#080C10] relative overflow-hidden"
    >
      {/* Background grain */}
      <div className="absolute inset-0 opacity-[0.015]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")`,
      }} />

      <div className="max-w-5xl mx-auto relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-[#C9A84C] mb-4" data-testid="timeline-title">
            THE WATER REMEMBERS
          </h2>
          <p className="font-['Outfit'] text-[#E8E0D0]/40 text-base tracking-wide">
            Nine eras. Over a hundred years. One inheritance.
          </p>
        </motion.div>

        {/* Era Navigation Bar */}
        <div className="flex items-center justify-center gap-1 mb-10">
          <button onClick={() => scrollToEra('prev')} disabled={activeEra === 1}
            className="p-2 text-[#E8E0D0]/20 hover:text-[#C9A84C] transition-colors disabled:opacity-20"
            data-testid="timeline-prev">
            <ChevronLeft size={20} />
          </button>
          <div className="flex gap-1">
            {eras.map(era => (
              <button
                key={era.id}
                onClick={() => !era.locked && setActiveEra(era.id)}
                className="relative px-3 py-2 rounded-lg transition-all duration-300"
                style={{
                  backgroundColor: activeEra === era.id ? `${ERA_COLORS[era.id]}15` : 'transparent',
                  borderColor: activeEra === era.id ? `${ERA_COLORS[era.id]}40` : '#1A253520',
                  border: `1px solid ${activeEra === era.id ? ERA_COLORS[era.id] + '40' : '#1A253520'}`,
                }}
                data-testid={`era-nav-${era.id}`}
              >
                <span className="font-['Outfit'] text-[9px] tracking-widest block"
                  style={{ color: activeEra === era.id ? ERA_COLORS[era.id] : era.locked ? '#555' : '#E8E0D060' }}>
                  ERA {era.id}
                </span>
                <span className="font-['Cormorant_Garamond'] text-xs font-bold block mt-0.5"
                  style={{ color: activeEra === era.id ? '#E8E0D0' : era.locked ? '#333' : '#E8E0D030' }}>
                  {era.years.split('–')[0]}
                </span>
                {era.locked && (
                  <Lock size={8} className="absolute top-1 right-1 text-[#555]" />
                )}
              </button>
            ))}
          </div>
          <button onClick={() => scrollToEra('next')} disabled={activeEra === 9}
            className="p-2 text-[#E8E0D0]/20 hover:text-[#C9A84C] transition-colors disabled:opacity-20"
            data-testid="timeline-next">
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Active Era Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeEra}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
          >
            {/* Era Header Card */}
            <div className="rounded-xl p-6 mb-8 relative overflow-hidden"
              style={{
                background: `linear-gradient(135deg, ${eraColor}08, transparent 60%)`,
                border: `1px solid ${eraColor}20`,
              }}>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-['Outfit'] text-[10px] tracking-[0.3em] px-2 py-0.5 rounded"
                      style={{ color: eraColor, backgroundColor: `${eraColor}10`, border: `1px solid ${eraColor}20` }}>
                      ERA {activeEra}
                    </span>
                    <span className="font-['Outfit'] text-[#E8E0D0]/30 text-xs">{currentEraData?.years}</span>
                    {currentEraData?.locked && <Lock size={12} className="text-[#555]" />}
                  </div>
                  <h3 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-bold mb-2"
                    style={{ color: currentEraData?.locked ? '#333' : '#E8E0D0' }}>
                    {currentEraData?.title}
                  </h3>
                  <div className="flex items-center gap-2">
                    <MapPin size={12} style={{ color: `${eraColor}80` }} />
                    <span className="font-['Outfit'] text-[#E8E0D0]/30 text-xs">{ERA_LOCATIONS[activeEra]}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-['Outfit'] text-[#E8E0D0]/15 text-[10px] tracking-widest">
                    {currentEvents.length} KEY EVENTS
                  </span>
                  {!currentEraData?.locked && (
                    <button onClick={() => setSelectedEra(currentEraData)}
                      className="px-3 py-1.5 rounded-lg font-['Outfit'] text-[9px] tracking-widest transition-colors hover:brightness-125"
                      style={{ backgroundColor: `${eraColor}15`, color: eraColor, border: `1px solid ${eraColor}30` }}
                      data-testid="read-full-story">
                      READ FULL STORY
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Events Timeline */}
            {currentEraData?.locked ? (
              <div className="text-center py-16">
                <Lock size={32} className="text-[#333] mx-auto mb-4" />
                <p className="font-['Cormorant_Garamond'] text-2xl text-[#333] italic">
                  {currentEraData.description}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-0">
                <div>
                  {currentEvents.filter((_, i) => i % 2 === 0).map((event, i) => (
                    <EventDot key={`${event.year}-${i}`} event={event} eraColor={eraColor} index={i} />
                  ))}
                </div>
                <div className="hidden md:block">
                  {currentEvents.filter((_, i) => i % 2 === 1).map((event, i) => (
                    <EventDot key={`${event.year}-${i}`} event={event} eraColor={eraColor} index={i} />
                  ))}
                </div>
                <div className="md:hidden">
                  {currentEvents.filter((_, i) => i % 2 === 1).map((event, i) => (
                    <EventDot key={`${event.year}-${i}`} event={event} eraColor={eraColor} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* Era Description Footer */}
            {!currentEraData?.locked && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="mt-8 p-5 rounded-lg bg-[#111820]/60 border border-[#1A2535]/40"
              >
                <p className="font-['Cormorant_Garamond'] text-[#E8E0D0]/40 text-base italic leading-relaxed">
                  "{currentEraData?.description}"
                </p>
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Progress bar */}
        <div className="mt-12 flex items-center gap-2">
          <span className="font-['Outfit'] text-[#E8E0D0]/10 text-[9px]">1919</span>
          <div className="flex-1 h-px bg-[#1A2535] relative">
            <motion.div
              className="absolute top-0 left-0 h-full"
              style={{ backgroundColor: eraColor }}
              animate={{ width: `${((activeEra - 1) / 8) * 100}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>
          <span className="font-['Outfit'] text-[#E8E0D0]/10 text-[9px]">2026</span>
        </div>
      </div>

      {/* Full Story Modal */}
      <AnimatePresence>
        {selectedEra && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/85 backdrop-blur-md z-50 flex items-center justify-center p-6"
            onClick={() => setSelectedEra(null)}
            data-testid="era-modal-overlay"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-[#0a0c10] border rounded-xl p-8 max-w-2xl w-full relative max-h-[80vh] overflow-y-auto"
              style={{ borderColor: `${ERA_COLORS[selectedEra.id]}30` }}
              onClick={(e) => e.stopPropagation()}
              data-testid="era-modal"
            >
              <button
                onClick={() => setSelectedEra(null)}
                className="absolute top-4 right-4 text-[#E8E0D0]/30 hover:text-[#C9A84C] transition-colors"
                data-testid="era-modal-close"
              >
                <X size={20} />
              </button>

              <div className="mb-6">
                <span className="font-['Outfit'] text-[10px] tracking-[0.3em]"
                  style={{ color: ERA_COLORS[selectedEra.id] }}>
                  ERA {selectedEra.id} — {selectedEra.years}
                </span>
                <h3 className="font-['Cormorant_Garamond'] text-3xl font-bold text-[#E8E0D0] mt-2">
                  {selectedEra.title}
                </h3>
                <div className="flex items-center gap-2 mt-2">
                  <MapPin size={12} style={{ color: `${ERA_COLORS[selectedEra.id]}80` }} />
                  <span className="font-['Outfit'] text-[#E8E0D0]/30 text-xs">{ERA_LOCATIONS[selectedEra.id]}</span>
                </div>
              </div>

              <p className="font-['Outfit'] text-[#E8E0D0]/70 leading-relaxed mb-6">
                {selectedEra.description}
              </p>

              {/* Events list in modal */}
              {(ERA_EVENTS[selectedEra.id] || []).length > 0 && (
                <div className="border-t pt-6" style={{ borderColor: `${ERA_COLORS[selectedEra.id]}15` }}>
                  <p className="font-['Outfit'] text-[10px] tracking-widest mb-4"
                    style={{ color: `${ERA_COLORS[selectedEra.id]}60` }}>
                    KEY EVENTS
                  </p>
                  <div className="space-y-3">
                    {(ERA_EVENTS[selectedEra.id] || []).map((evt, i) => {
                      const Icon = evt.icon;
                      const dotColor = TYPE_COLORS[evt.type] || ERA_COLORS[selectedEra.id];
                      return (
                        <div key={i} className="flex items-start gap-3">
                          <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                            style={{ backgroundColor: `${dotColor}15`, border: `1px solid ${dotColor}30` }}>
                            <Icon size={9} style={{ color: dotColor }} />
                          </div>
                          <div>
                            <span className="font-['Outfit'] text-[9px] tracking-widest" style={{ color: `${ERA_COLORS[selectedEra.id]}60` }}>
                              {evt.year}
                            </span>
                            <p className="font-['Outfit'] text-[#E8E0D0]/60 text-sm">{evt.event}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default TimelineSection;
